// Recettes (lot L2-A) : paiements (H4-05, H4-06), notifications des prestataires, quittances (H4-08)
// et remboursements (H4-10). Un paiement en ligne a une référence unique émise par le serveur ; la
// confirmation du prestataire est vérifiée côté serveur (signature, référence, montant, devise,
// bénéficiaire) et chaque notification est journalisée avec son traitement. Aucun prestataire réel
// n'est configuré : l'adaptateur simulateur sert au développement et à la présentation, il est
// refusé en production (R20).
import { z } from 'zod';

const uuid = z.string().uuid('Identifiant invalide');
const jour = z.iso.date('Date AAAA-MM-JJ attendue');

export const CANAUX_PAIEMENT = ['simulateur', 'mobile_money', 'banque', 'virement', 'terminal', 'caisse'] as const;
export type CanalPaiement = (typeof CANAUX_PAIEMENT)[number];

export const ETATS_PAIEMENT = ['initie', 'en_attente', 'confirme', 'refuse', 'expire', 'annule', 'rembourse'] as const;
export type EtatPaiement = (typeof ETATS_PAIEMENT)[number];

export const TRAITEMENTS_NOTIFICATION = ['appliquee', 'ignoree_doublon', 'tardive', 'contradictoire', 'invalide'] as const;
export type TraitementNotification = (typeof TRAITEMENTS_NOTIFICATION)[number];

export const ETATS_REMBOURSEMENT = ['propose', 'valide', 'ordonne', 'rejete'] as const;

/** Statuts annoncés par un prestataire dans une notification. */
export const STATUTS_PRESTATAIRE = ['confirme', 'refuse', 'expire'] as const;
export type StatutPrestataire = (typeof STATUTS_PRESTATAIRE)[number];

/** Actions de la route de simulation (développement et présentation seulement). */
export const ACTIONS_SIMULATEUR = ['confirmer', 'refuser', 'expirer', 'rejouer'] as const;
export type ActionSimulateur = (typeof ACTIONS_SIMULATEUR)[number];

/** Format de la référence serveur d'un paiement. */
export const FORMAT_REFERENCE_PAIEMENT = /^PAY-[A-Z0-9]{16}$/;

/** Paiement initié par l'usager depuis le portail : montant par défaut, le reste de la créance. */
export const schemaPaiementUsager = z.strictObject({
  montant: z.number().int().positive().optional(),
  prestataire: z.string().regex(/^[a-z][a-z0-9_]{1,62}$/).optional(),
});

/** Paiement en ligne initié par un agent (guichet) sur une ou plusieurs créances d'un même redevable. */
export const schemaPaiementAgent = z.strictObject({
  creance_ids: z.array(uuid).min(1).max(20),
  montant: z.number().int().positive(),
  prestataire: z.string().regex(/^[a-z][a-z0-9_]{1,62}$/).optional(),
});

/** Notification signée envoyée par le simulateur (et forme commune attendue des adaptateurs). */
export const schemaNotificationSimulateur = z.object({
  reference_serveur: z.string().max(40),
  reference_prestataire: z.string().max(100),
  statut: z.enum(STATUTS_PRESTATAIRE),
  montant: z.number().int(),
  devise: z.string().max(3),
  beneficiaire: z.string().max(64),
  horodatage: z.string().max(40),
  motif: z.string().max(500).optional(),
});
export type NotificationSimulateur = z.infer<typeof schemaNotificationSimulateur>;

export const schemaReferencePaiement = z.object({ reference: z.string().regex(FORMAT_REFERENCE_PAIEMENT, 'Référence de paiement invalide') });
export const schemaActionSimulateur = schemaReferencePaiement.extend({ action: z.enum(ACTIONS_SIMULATEUR) });
export const schemaCorpsSimulateur = z.strictObject({ motif: z.string().trim().max(500).optional() });

export const schemaFiltrePaiements = z.object({
  etat: z.enum(ETATS_PAIEMENT).optional(),
  canal: z.enum(CANAUX_PAIEMENT).optional(),
  creance_id: uuid.optional(),
  en_anomalie: z.enum(['oui', 'non']).optional(),
  debut: jour.optional(),
  fin: jour.optional(),
  limite: z.coerce.number().int().min(1).max(1000).default(200),
});

export const schemaAnnulationPaiement = z.strictObject({ motif: z.string().trim().min(3).max(1000) });

export const schemaPropositionRemboursement = z.strictObject({
  paiement_id: uuid,
  montant: z.number().int().positive(),
  motif: z.string().trim().min(3).max(1000),
  piece_document_id: uuid.optional(),
});

export const schemaRejetRemboursement = z.strictObject({ motif: z.string().trim().min(3).max(1000) });

export const schemaFiltreRemboursements = z.object({
  etat: z.enum(ETATS_REMBOURSEMENT).optional(),
  paiement_id: uuid.optional(),
});

export const schemaFiltreQuittances = z.object({
  paiement_id: uuid.optional(),
  creance_id: uuid.optional(),
  etat: z.enum(['valide', 'annulee']).optional(),
  limite: z.coerce.number().int().min(1).max(1000).default(200),
});

export const schemaPaiementReponse = z.object({
  id: z.string(),
  reference_serveur: z.string(),
  canal: z.string(),
  prestataire: z.string(),
  reference_prestataire: z.string().nullable(),
  montant: z.number(),
  devise: z.literal('XAF'),
  etat: z.string(),
  en_anomalie: z.boolean(),
  creances_visees: z.array(z.string()),
  initie_le: z.string(),
  confirme_le: z.string().nullable(),
  recu_serveur_le: z.string().nullable(),
  refuse_le: z.string().nullable(),
  motif_refus: z.string().nullable(),
  quittance: z.object({ id: z.string(), numero: z.string(), code_verification: z.string(), etat: z.string() }).nullable(),
});
export type PaiementReponse = z.infer<typeof schemaPaiementReponse>;

export const schemaQuittanceReponse = z.object({
  id: z.string(),
  numero: z.string(),
  paiement_id: z.string(),
  reference_paiement: z.string(),
  montant: z.number(),
  rubrique: z.string(),
  emise_le: z.string(),
  code_verification: z.string(),
  etat: z.string(),
  motif_annulation: z.string().nullable(),
  pdf_disponible: z.boolean(),
  mention_environnement: z.string().nullable(),
});
export type QuittanceReponse = z.infer<typeof schemaQuittanceReponse>;
